import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { authService } from '../../services/authService';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Paper,
  Button,
  Tooltip,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Save,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { FieldMappingItem } from '../../models';
import OverlayFormField from './OverlayFormField';

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PDFOverlayFormProps {
  pdfUrl: string;
  fields: FieldMappingItem[];
  formData: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
  onSave?: () => void;
  width?: number;
  readOnly?: boolean;
}

export const PDFOverlayForm: React.FC<PDFOverlayFormProps> = ({
  pdfUrl,
  fields,
  formData,
  onChange,
  onSave,
  width = 800,
  readOnly = false,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [showFields, setShowFields] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback(
    (page: { width: number; height: number; originalWidth?: number; originalHeight?: number }) => {
      // react-pdf Page의 onLoadSuccess는 렌더링된 크기와 원본 크기를 반환
      // 원본 크기를 사용하여 정확한 비율 계산
      const origWidth = page.originalWidth || page.width;
      const origHeight = page.originalHeight || page.height;
      setPageSize({ width: origWidth, height: origHeight });
    },
    []
  );

  const changePage = useCallback(
    (offset: number) => {
      const newPage = pageNumber + offset;
      if (newPage >= 1 && newPage <= numPages) {
        setPageNumber(newPage);
      }
    },
    [pageNumber, numPages]
  );

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  // 현재 페이지에 해당하는 필드만 필터링 (페이지별 필드 매핑 지원 시)
  const currentPageFields = fields.filter((field) => field.position);

  // 실제 렌더링되는 PDF 크기 계산
  const scaledWidth = width * scale;
  const aspectRatio = pageSize.height / pageSize.width || 1.414; // A4 기본 비율
  const scaledHeight = scaledWidth * aspectRatio;

  // PDF 파일 객체 (인증 헤더 포함)
  const pdfFile = useMemo(() => {
    const token = authService.getAccessToken();
    if (!token) return pdfUrl;
    return {
      url: pdfUrl,
      httpHeaders: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [pdfUrl]);

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      {/* 컨트롤 바 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          p: 1,
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => changePage(-1)} disabled={pageNumber <= 1} size="small">
            <NavigateBefore />
          </IconButton>
          <Typography variant="body2">
            {pageNumber} / {numPages}
          </Typography>
          <IconButton onClick={() => changePage(1)} disabled={pageNumber >= numPages} size="small">
            <NavigateNext />
          </IconButton>
          <Box sx={{ mx: 1, borderLeft: '1px solid', borderColor: 'grey.300', height: 24 }} />
          <IconButton onClick={zoomOut} disabled={scale <= 0.5} size="small">
            <ZoomOut />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton onClick={zoomIn} disabled={scale >= 3.0} size="small">
            <ZoomIn />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={showFields ? '입력 필드 숨기기' : '입력 필드 보이기'}>
            <IconButton onClick={() => setShowFields((prev) => !prev)} size="small">
              {showFields ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </Tooltip>
          {onSave && !readOnly && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={onSave}
            >
              저장
            </Button>
          )}
        </Box>
      </Box>

      {/* PDF 및 오버레이 폼 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          overflow: 'auto',
          maxHeight: 'calc(100vh - 300px)',
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            width: scaledWidth,
            height: scaledHeight,
            border: '1px solid',
            borderColor: 'grey.300',
            bgcolor: 'white',
            boxShadow: 2,
          }}
        >
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: scaledWidth,
                height: 400,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Document
            file={pdfFile}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={null}
            error={
              <Box sx={{ p: 4, textAlign: 'center', width: scaledWidth }}>
                <Typography color="error">PDF를 불러올 수 없습니다.</Typography>
              </Box>
            }
          >
            <Page
              pageNumber={pageNumber}
              width={scaledWidth}
              onLoadSuccess={onPageLoadSuccess}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>

          {/* 오버레이 입력 필드 */}
          {!loading && showFields && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: readOnly ? 'none' : 'auto',
              }}
            >
              {currentPageFields.map((field) => (
                <OverlayFormField
                  key={field.field_id}
                  field={field}
                  value={formData[field.field_id] || ''}
                  onChange={onChange}
                  containerWidth={scaledWidth}
                  containerHeight={scaledHeight}
                  disabled={readOnly}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* 필드 리스트 (디버깅/확인용) */}
      {!showFields && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            입력 필드 목록
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {fields.map((field) => (
              <Typography key={field.field_id} variant="caption" sx={{ px: 1, py: 0.5, bgcolor: 'primary.light', color: 'white', borderRadius: 1 }}>
                {field.label}: {formData[field.field_id] || '(미입력)'}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PDFOverlayForm;
